// v2 harbour — a stone quay on a timber pier: the plank deck spans the whole 2x2
// plot low over the water on pilings, carrying a raised dressed-stone quay block
// in the middle with stone steps down to the pier, a harbourmaster's hut with a
// lifebuoy on the wall, a little harbour-light tower with an amber lantern, a
// luffing crane davit swinging a crate out over the corner, mooring bollards
// along the pier edges, and stacked cargo. Footprint-native 2x2. Ported 1:1 from
// age-of-mcp's v2/harbour.ts, using the library's shared `foundationSlab`,
// `scaffold`, `barrel` and `estate` (cargoCrate, bollard) helpers.
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, barrel, estate, EFFECT_LOOP, type Part } from "../../lib";

const DECK    = "#9a7448";   // pier planking
const PILING  = "#5e4528";   // pilings under the deck
const QUAY    = "#84827a";   // dressed quay stone
const QUAY_DK = "#6b6962";   // quay trim course
const HUT     = "#8a5a32";   // harbourmaster's hut
const ROOF    = "#5f3a1c";
const DOOR    = "#3a2a18";
const LIGHT   = "#9a9890";   // light-tower stone
const LANTERN = "#e8b54a";   // lantern glass
const MAST    = "#55504a";   // davit ironwork
const BUOY    = "#e8e2d2";   // lifebuoy ring

const DECK_TOP = 0.35;       // pier deck top
const QUAY_TOP = 0.49;       // quay platform top

// The pier: a plank deck spanning the whole plot on six pilings.
function pier(): Part[] {
  const parts: Part[] = [
    { geometry: new THREE.BoxGeometry(1.84, 0.1, 1.84), color: DECK,
      position: [0, 0.3, 0] },
  ];
  const spots: [number, number][] = [
    [-0.8, -0.8], [0.8, -0.8], [-0.8, 0.8], [0.8, 0.8], [-0.8, 0], [0.8, 0],
  ];
  for (const [x, z] of spots) {
    parts.push({ geometry: new THREE.CylinderGeometry(0.07, 0.07, 0.35, 5),
      color: PILING, position: [x, 0.175, z] });
  }
  return parts;
}

// The raised stone quay block at the heart of the pier.
function quay(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(1.06, 0.44, 1.06), color: QUAY,
      position: [0, 0.22, 0] },
    { geometry: new THREE.BoxGeometry(1.12, 0.05, 1.12), color: QUAY_DK,
      position: [0, QUAY_TOP - 0.025, 0] },
  ];
}

// Stone steps from the quay top down to the pier deck, on two sides.
function steps(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.34, 0.07, 0.14), color: QUAY_DK,
      position: [0, 0.385, 0.6] },
    { geometry: new THREE.BoxGeometry(0.14, 0.07, 0.34), color: QUAY_DK,
      position: [-0.6, 0.385, 0] },
  ];
}

// The harbourmaster's hut on the quay, with its door and lifebuoy.
function hut(): Part[] {
  const x = -0.28, z = -0.28;
  return [
    { geometry: new THREE.BoxGeometry(0.46, 0.4, 0.46), color: HUT,
      position: [x, QUAY_TOP + 0.2, z] },
    { geometry: new THREE.ConeGeometry(0.36, 0.24, 4), color: ROOF,
      position: [x, QUAY_TOP + 0.52, z], rotation: [0, Math.PI / 4, 0] },
    { geometry: new THREE.BoxGeometry(0.16, 0.26, 0.03), color: DOOR,
      position: [x - 0.08, QUAY_TOP + 0.13, z + 0.24] },
    // The lifebuoy ring hanging beside the door.
    { geometry: new THREE.TorusGeometry(0.075, 0.022, 6, 8), color: BUOY,
      position: [x + 0.14, QUAY_TOP + 0.23, z + 0.24] },
    // A porthole window on the seaward wall.
    { geometry: new THREE.BoxGeometry(0.03, 0.1, 0.1), color: LANTERN,
      position: [x - 0.235, QUAY_TOP + 0.23, z] },
  ];
}

// The harbour light: a small stone tower with a gallery, an amber lantern
// and a conical cap.
function harbourLight(): Part[] {
  const x = 0.3, z = 0.3;
  return [
    { geometry: new THREE.CylinderGeometry(0.085, 0.105, 0.5, 6), color: LIGHT,
      position: [x, QUAY_TOP + 0.25, z] },
    { geometry: new THREE.CylinderGeometry(0.12, 0.12, 0.03, 6), color: QUAY_DK,
      position: [x, QUAY_TOP + 0.515, z] },
    { geometry: new THREE.BoxGeometry(0.11, 0.1, 0.11), color: LANTERN,
      position: [x, QUAY_TOP + 0.58, z] },
    { geometry: new THREE.ConeGeometry(0.1, 0.1, 6), color: ROOF,
      position: [x, QUAY_TOP + 0.68, z] },
  ];
}

// The luffing davit on the pier corner, swinging a crate out on its rope.
// Rope and crate are named so the "active" animation can wind them up and down.
function davit(): Part[] {
  const x = 0.62, z = -0.62;
  return [
    { geometry: new THREE.CylinderGeometry(0.035, 0.045, 0.55, 6), color: MAST,
      position: [x, DECK_TOP + 0.275, z] },
    // The boom points diagonally outward over the corner (+x, -z).
    { geometry: new THREE.BoxGeometry(0.5, 0.05, 0.05), color: MAST,
      position: [x + 0.13, DECK_TOP + 0.57, z - 0.13], rotation: [0, Math.PI / 4, 0] },
    { geometry: new THREE.BoxGeometry(0.016, 0.32, 0.016), color: "#222",
      position: [x + 0.26, DECK_TOP + 0.39, z - 0.26], name: "davit-rope" },
    ...estate.cargoCrate(x + 0.26, DECK_TOP + 0.11, z - 0.26, 0.2)
      .map((p): Part => ({ ...p, name: "davit-load" })),
  ];
}

function stage(s: number): Part[] {
  if (s <= 0) return [foundationSlab(1.84)];
  if (s === 1) {
    return [foundationSlab(1.84),
      ...pier().slice(1),                  // pilings only, no deck yet
      ...scaffold(1.7, 0.5)];
  }
  if (s === 2) {
    return [foundationSlab(1.84), ...pier(),
      { geometry: new THREE.BoxGeometry(1.06, 0.44, 1.06), color: QUAY,
        position: [0, 0.22, 0] },
      ...scaffold(1.7, 0.8)];
  }
  return [
    foundationSlab(1.84),
    ...pier(),
    ...quay(),
    ...steps(),
    ...hut(),
    ...harbourLight(),
    ...davit(),
    ...estate.bollard(0.84, DECK_TOP, 0, 0.16, "#42351f"),
    ...estate.bollard(-0.84, DECK_TOP, 0, 0.16, "#42351f"),
    ...estate.bollard(0, DECK_TOP, 0.84, 0.16, "#42351f"),
    ...estate.bollard(0, DECK_TOP, -0.84, 0.16, "#42351f"),
    ...estate.cargoCrate(0.55, DECK_TOP, 0.6, 0.24),
    ...estate.cargoCrate(0.32, DECK_TOP, 0.72, 0.17),
    barrel(-0.7, 0.55, DECK_TOP, 0.28, 0.115),
  ];
}

export const harbourV2 = defineModel({
  id: "harbour-v2",
  tiles: { w: 2, h: 2 },
  batchId: "fable",
  name: "Harbour (v2)",
  tags: ["building","harbour","low-poly","age-of-mcp","fable","water","animated"],
  summary: "Low-poly harbour.",
  description: "A timber dock reaching out over the water from the shore.",
  author: "age-of-mcp",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => stage(0) },
    { id: "stage-1", label: "Lower", parts: () => stage(1) },
    { id: "stage-2", label: "Upper", parts: () => stage(2) },
    { id: "complete", label: "Complete", parts: () => stage(3) },
  ],
  animations: [
    {
      // The davit winches its crate up off the deck — the crate rises 0.16
      // while the rope winds up about its top end at the boom (y = 0.90),
      // then lowers it back.
      id: "active",
      label: "Davit hoisting",
      duration: EFFECT_LOOP,
      variants: ["complete"],
      tracks: [
        {
          target: "davit-load",
          pivot: [0.88, 0.46, -0.88],
          position: [
            { t: 0, value: [0, 0, 0] },
            { t: EFFECT_LOOP / 2, value: [0, 0.16, 0], ease: "easeInOut" },
            { t: EFFECT_LOOP, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
        {
          target: "davit-rope",
          pivot: [0.88, 0.90, -0.88],
          scale: [
            { t: 0, value: [1, 1, 1] },
            { t: EFFECT_LOOP / 2, value: [1, 0.5, 1], ease: "easeInOut" },
            { t: EFFECT_LOOP, value: [1, 1, 1], ease: "easeInOut" },
          ],
        },
      ],
    },
  ],
});
